import { cva, type VariantProps } from 'class-variance-authority';
import { Check, Copy } from 'lucide-react';
import type * as React from 'react';
import { Button } from '@/components/atoms/buttons/Button';
import { CodeSnippet } from '@/components/molecules/data/CodeSnippet';
import { COPY_ICON_SLOT_CLASS } from '@/components/molecules/data/CodeSnippet/CodeSnippet';
import { SUGAR_HIGH_STYLE } from '@/lib/syntax-highlight';
import { cn } from '@/lib/utils';

// =============================================================================
// Variants
// =============================================================================

const codeBlockVariants = cva(
  'relative overflow-hidden rounded-xl border border-border bg-muted',
  {
    variants: {
      size: {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

// =============================================================================
// Code Block Header
// =============================================================================

export interface AICodeBlockHeaderProps {
  /** Programming language */
  language?: string;
  /** File name to display */
  fileName?: string;
  /** Whether to show copy button */
  showCopyButton?: boolean;
  /** Callback when copy is clicked */
  onCopy?: () => void;
  /** Whether content was copied */
  copied?: boolean;
  /** Additional class name */
  className?: string;
  /** Extra actions to render */
  actions?: React.ReactNode;
}

function AICodeBlockHeader({
  language,
  fileName,
  showCopyButton = true,
  onCopy,
  copied = false,
  className,
  actions,
}: AICodeBlockHeaderProps) {
  return (
    <div
      data-slot="ai-code-block-header"
      className={cn(
        'flex items-center justify-between px-4 py-2 border-b border-border bg-accent',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        {fileName && (
          <span className="text-sm font-medium text-secondary-foreground">
            {fileName}
          </span>
        )}
        {language && !fileName && (
          <span className="text-xs font-medium text-secondary-foreground uppercase">
            {language}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        {showCopyButton && (
          <Button
            appearance="ghost"
            variant="secondary"
            size="sm"
            onClick={onCopy}
            aria-label={
              copied ? 'Copied to clipboard' : 'Copy code to clipboard'
            }
            className="h-7 px-2 text-secondary-foreground hover:text-foreground transition-colors duration-150 ease-out"
          >
            <span
              data-slot="ai-code-block-copy-icon"
              aria-hidden="true"
              className={COPY_ICON_SLOT_CLASS}
            >
              <Copy className={copied ? 'opacity-0' : 'opacity-100'} />
              <Check
                className={cn(
                  'text-success',
                  copied ? 'opacity-100' : 'opacity-0',
                )}
              />
            </span>
            {/* Hidden sizer reserves the longer label so the button width is stable. */}
            <span className="ms-1.5 inline-grid text-xs">
              <span className="col-start-1 row-start-1">
                {copied ? 'Copied!' : 'Copy'}
              </span>
              <span
                aria-hidden="true"
                className="invisible col-start-1 row-start-1"
              >
                Copied!
              </span>
            </span>
          </Button>
        )}
      </div>
    </div>
  );
}

AICodeBlockHeader.displayName = 'AICodeBlockHeader';

// =============================================================================
// Code Block Content
// =============================================================================

export interface AICodeBlockContentProps {
  /** Code content */
  children: React.ReactNode;
  /** Whether to show line numbers */
  showLineNumbers?: boolean;
  /** Starting line number */
  startLineNumber?: number;
  /** Additional class name */
  className?: string;
}

interface AICodeBlockContentBodyProps {
  children: React.ReactNode;
  showLineNumbers: boolean;
  startLineNumber: number;
}

function AICodeBlockContentBody({
  children,
  showLineNumbers,
  startLineNumber,
}: AICodeBlockContentBodyProps): React.ReactNode {
  if (showLineNumbers && typeof children === 'string') {
    const lines = children.split('\n');
    return (
      <div className="flex">
        <div className="flex flex-col items-end pe-4 select-none border-e border-border me-4">
          {lines.map((_, index) => (
            <span
              key={index}
              className="text-secondary-foreground leading-relaxed"
            >
              {startLineNumber + index}
            </span>
          ))}
        </div>
        <section
          className="flex-1 overflow-x-auto [overflow-wrap:normal] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          aria-label="Code content"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: Focusable scroll region satisfies axe scrollable-region-focusable.
          tabIndex={0}
        >
          <pre className="m-0 [overflow-wrap:normal]">
            <code>{children}</code>
          </pre>
        </section>
      </div>
    );
  }

  return (
    <section
      className="overflow-x-auto [overflow-wrap:normal] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      aria-label="Code content"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: Focusable scroll region satisfies axe scrollable-region-focusable.
      tabIndex={0}
    >
      <pre className="m-0 [overflow-wrap:normal]">
        <code>{children}</code>
      </pre>
    </section>
  );
}

function AICodeBlockContent({
  children,
  showLineNumbers = false,
  startLineNumber = 1,
  className,
}: AICodeBlockContentProps) {
  return (
    <div
      data-slot="ai-code-block-content"
      className={cn('p-4 text-sm font-mono leading-relaxed', className)}
    >
      <AICodeBlockContentBody
        showLineNumbers={showLineNumbers}
        startLineNumber={startLineNumber}
      >
        {children}
      </AICodeBlockContentBody>
    </div>
  );
}

AICodeBlockContent.displayName = 'AICodeBlockContent';

// =============================================================================
// Main AICodeBlock Component
// =============================================================================

export interface AICodeBlockProps
  extends VariantProps<typeof codeBlockVariants> {
  /** Code content to display */
  code: string;
  /** Programming language for syntax highlighting */
  language?: string;
  /** File name to display in header */
  fileName?: string;
  /** Whether to show the header */
  showHeader?: boolean;
  /** Whether to show copy button */
  showCopyButton?: boolean;
  /** Whether to show line numbers */
  showLineNumbers?: boolean;
  /** Starting line number */
  startLineNumber?: number;
  /** Pre-highlighted HTML content */
  highlightedCode?: string;
  /** Additional class name */
  className?: string;
  /** Extra actions for the header */
  headerActions?: React.ReactNode;
}

function AICodeBlock({
  code,
  language = 'plaintext',
  fileName,
  showHeader = true,
  showCopyButton = true,
  showLineNumbers = false,
  startLineNumber = 1,
  highlightedCode,
  size,
  className,
  headerActions,
}: AICodeBlockProps) {
  return (
    <div
      data-slot="ai-code-block"
      className={cn(codeBlockVariants({ size }), className)}
      style={SUGAR_HIGH_STYLE}
    >
      <CodeSnippet
        code={code}
        language={language}
        fileName={fileName}
        showHeader={showHeader}
        showCopyButton={showCopyButton}
        showLineNumbers={showLineNumbers}
        startLineNumber={startLineNumber}
        highlightedCode={highlightedCode}
        size={size}
        headerActions={headerActions}
        className="border-0 bg-transparent"
      />
    </div>
  );
}

AICodeBlock.displayName = 'AICodeBlock';

export {
  AICodeBlock,
  AICodeBlockContent,
  AICodeBlockHeader,
  codeBlockVariants,
};
